import { Flexbox, Form } from '@/components'
import yup from '@/lib/yupPt'
import clinicalCareClassifications from '@/services/api/clinicalCareClassifications'
import clinicalCarePriorities from '@/services/api/clinicalCarePriorities'
import PropTypes from 'prop-types'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { toast } from 'react-toastify'
import ClassificationFields from './ClassificationFields'
import QuestionnaireFields from './QuestionnaireFields'

const schema = yup.object().shape({
  description: yup.string().trim().required().min(5),
  priorityId: yup.number().required(),
  questionnaire: yup.object().shape({
    questions: yup.array().of(yup.object().shape({
      description: yup.string().trim().required(),
      allowAttachments: yup.boolean(),
      required: yup.boolean(),
    })),
  }),
})

const getInitialValues = (classification) => ({
  description: classification?.description ?? '',
  priorityId: classification?.priorityId ?? '',
  questionnaire: {
    questions: (classification?.questionnaire?.questions ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((question) => ({
        localId: String(question?.id ?? `${Date.now()}-${Math.random()}`),
        description: question?.description ?? '',
        allowAttachments: Boolean(question?.allowAttachments),
        required: Boolean(question?.required),
      })),
  },
})

const getSubmitValues = (values) => {
  const questions = values.questionnaire.questions
    .map((question, index) => ({
      description: question.description.trim(),
      order: index,
      allowAttachments: Boolean(question.allowAttachments),
      required: Boolean(question.required),
    }))
    .filter((question) => question.description)

  return {
    description: values.description,
    priorityId: values.priorityId,
    questionnaire: questions.length ? { questions } : null,
  }
}

function FormClassification({
  btnClass, btnIcon, btnText, classification, onApiResponse,
}) {
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const [priorities, setPriorities] = useState([])
  const [fetchingPriorities, setFetchingPriorities] = useState(false)

  const fetchPriorities = useCallback(async () => {
    try {
      setFetchingPriorities(true)
      const { data } = await clinicalCarePriorities.all()
      setPriorities(data)
    } finally {
      setFetchingPriorities(false)
    }
  }, [])

  useLayoutEffect(() => {
    if (active) {
      fetchPriorities()
    }
  }, [active, fetchPriorities])

  const onSave = useCallback(async (values) => {
    const submitValues = getSubmitValues(values)
    const params = classification ? [classification.id, submitValues] : [submitValues]
    const method = classification
    ? clinicalCareClassifications.update : clinicalCareClassifications.create
    try {
      setLoading(true)
      const { data } = await method(...params)
      toast.success(`Classificação ${classification ? 'atualizada' : 'criada'} com sucesso`)
      onApiResponse(data)
      setLoading(false)
      setActive(false)
    } catch (e) {
      setLoading(false)
      toast.error(e.message)
    }
  }, [onApiResponse, classification])

  return (
    <>
      <button
        type="button"
        className={btnClass}
        onClick={() => setActive(true)}
      >
        <i className={btnIcon} />
        {btnText ? ` ${btnText}` : ''}
      </button>
      {
        active && (

          <fieldset disabled={loading}>
            <Form.Formik
              initialValues={getInitialValues(classification)}
              onSubmit={onSave}
              validationSchema={schema}
              validateOnMount
            >
              {
                ({
                  touched, errors, isValid, values, setFieldValue,
                }) => (
                  <Form.ValidationForm>
                    <div className={`modal ${active ? 'active' : ''} text-left`}>
                      <a type="button" className="modal-overlay" aria-label="Close" onClick={() => setActive(false)} />
                      <div className="modal-container" style={{ maxWidth: 760 }}>
                        <div className="modal-header">
                          <a
                            type="button"
                            className="btn btn-clear float-right"
                            onClick={() => setActive(false)}
                          />
                          <div className="modal-title h5">
                            {classification ? 'Editar classificação' : 'Nova classificação'}
                          </div>
                        </div>
                        <div className="modal-body">
                          <Flexbox.Columns>
                            <ClassificationFields
                              errors={errors}
                              fetchingPriorities={fetchingPriorities}
                              priorities={priorities}
                              touched={touched}
                            />
                            <QuestionnaireFields
                              errors={errors}
                              setFieldValue={setFieldValue}
                              touched={touched}
                              values={values}
                            />
                          </Flexbox.Columns>
                        </div>
                        <div className="modal-footer">
                          <button type="submit" disabled={!isValid} className={`btn btn-primary ${loading ? 'loading' : ''}`}>
                            <i className="fas fa-save" />
                            &nbsp;Salvar
                          </button>
                        </div>
                      </div>
                    </div>
                  </Form.ValidationForm>
                )
              }
            </Form.Formik>
          </fieldset>
        )
      }
    </>
  )
}

FormClassification.propTypes = {
  btnClass: PropTypes.string.isRequired,
  btnIcon: PropTypes.string.isRequired,
  onApiResponse: PropTypes.func.isRequired,
  classification: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
    priorityId: PropTypes.number,
    questionnaire: PropTypes.objectOf(Object),
  }),
  btnText: PropTypes.string,
}

FormClassification.defaultProps = {
  classification: null,
  btnText: '',
}

export default FormClassification
